import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AssistantsPanel = () => {
  const [assistants, setAssistants] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', pin: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [aRes, mRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/assistants`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/assistant-metrics`, { withCredentials: true }),
      ]);
      setAssistants(aRes.data);
      setMetrics(mRes.data);
    } catch { }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setError('');
    if (!form.name || !form.email) return setError('Nombre y email son requeridos');
    if (!editId && (!form.password || !form.pin)) return setError('Password y PIN son requeridos para nuevos asistentes');
    if (form.pin && (form.pin.length !== 4 || !/^\d{4}$/.test(form.pin))) return setError('PIN debe ser exactamente 4 digitos');
    setLoading(true);
    try {
      if (editId) {
        const payload = { name: form.name, email: form.email };
        if (form.pin) payload.pin = form.pin;
        if (form.password) payload.password = form.password;
        await axios.put(`${API_URL}/api/admin/assistants/${editId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/admin/assistants`, form, { withCredentials: true });
      }
      setShowForm(false); setEditId(null); setForm({ name: '', email: '', password: '', pin: '' });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error');
    }
    setLoading(false);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Eliminar asistente ${name}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/assistants/${id}`, { withCredentials: true });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  const getMetric = (id) => metrics.find(m => m.id === id);

  const inputStyle = {
    width: '100%', padding: '10px 14px', background: '#1a1a1e', border: '1px solid #333',
    borderRadius: 8, color: '#fff', fontSize: 14, outline: 'none',
  };

  return (
    <div data-testid="assistants-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ color: '#fff', fontSize: 22, fontWeight: 700 }}>Gestion de Asistentes</h2>
          <p style={{ color: '#666', fontSize: 13, marginTop: 4 }}>Crear, editar y monitorear asistentes</p>
        </div>
        <button data-testid="new-assistant-btn" onClick={() => { setShowForm(true); setEditId(null); setForm({ name: '', email: '', password: '', pin: '' }); }}
          style={{ padding: '10px 20px', background: '#E8720C', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          <i className="fas fa-plus" style={{ marginRight: 8 }} />Nuevo Asistente
        </button>
      </div>

      {showForm && (
        <div data-testid="assistant-form" style={{ background: '#141416', borderRadius: 12, padding: 24, border: '1px solid #1e1e22', marginBottom: 24 }}>
          <h3 style={{ color: '#fff', fontSize: 16, marginBottom: 16 }}>{editId ? 'Editar Asistente' : 'Nuevo Asistente'}</h3>
          {error && <div style={{ color: '#f87171', fontSize: 13, marginBottom: 12, padding: 10, background: '#2e0a0a', borderRadius: 8 }}>{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={{ color: '#999', fontSize: 12, display: 'block', marginBottom: 4 }}>Nombre *</label>
              <input data-testid="assistant-name-input" style={inputStyle} value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Maria Lopez" />
            </div>
            <div>
              <label style={{ color: '#999', fontSize: 12, display: 'block', marginBottom: 4 }}>Email *</label>
              <input data-testid="assistant-email-input" style={inputStyle} type="email" value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="assistant@ogmusic.com" />
            </div>
            <div>
              <label style={{ color: '#999', fontSize: 12, display: 'block', marginBottom: 4 }}>
                Password {editId ? '(dejar vacio para no cambiar)' : '*'}
              </label>
              <input data-testid="assistant-password-input" style={inputStyle} type="password" value={form.password}
                onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Min 8 caracteres" />
            </div>
            <div>
              <label style={{ color: '#999', fontSize: 12, display: 'block', marginBottom: 4 }}>
                PIN 4 Digitos {editId ? '(dejar vacio para no cambiar)' : '*'}
              </label>
              <input data-testid="assistant-pin-input" style={{ ...inputStyle, letterSpacing: '0.3em', textAlign: 'center' }}
                maxLength={4} value={form.pin} inputMode="numeric"
                onChange={e => setForm(p => ({ ...p, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))} placeholder="****" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
            <button onClick={() => { setShowForm(false); setError(''); }} style={{
              padding: '8px 20px', background: '#333', border: 'none', borderRadius: 8, color: '#aaa', cursor: 'pointer', fontSize: 13 }}>
              Cancelar
            </button>
            <button data-testid="save-assistant-btn" onClick={handleSave} disabled={loading} style={{
              padding: '8px 20px', background: '#E8720C', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {assistants.map(a => {
          const m = getMetric(a.id);
          return (
            <div key={a.id} data-testid={`assistant-row-${a.id}`} style={{
              background: '#141416', borderRadius: 12, padding: 20, border: '1px solid #1e1e22',
              display: 'flex', alignItems: 'center', gap: 20,
            }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#E8720C22', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="fas fa-user" style={{ color: '#E8720C' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#fff', fontWeight: 600, fontSize: 15 }}>{a.name}</div>
                <div style={{ color: '#666', fontSize: 12 }}>{a.email}</div>
              </div>
              {m && (
                <div style={{ display: 'flex', gap: 16 }}>
                  {[
                    { label: 'Total', val: m.uploads?.total, color: '#E8720C' },
                    { label: 'Hoy', val: m.uploads?.today, color: '#60a5fa' },
                    { label: 'Semana', val: m.uploads?.week, color: '#a78bfa' },
                    { label: 'Aprobados', val: m.approvals?.approved, color: '#4ade80' },
                    { label: 'Pendientes', val: m.approvals?.pending, color: '#fbbf24' },
                  ].map((s, i) => (
                    <div key={i} style={{ textAlign: 'center' }}>
                      <div style={{ color: s.color, fontSize: 18, fontWeight: 700 }}>{s.val || 0}</div>
                      <div style={{ color: '#555', fontSize: 10 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 6 }}>
                <button data-testid={`edit-assistant-${a.id}`} onClick={() => {
                  setEditId(a.id); setForm({ name: a.name, email: a.email, password: '', pin: '' }); setShowForm(true);
                }} style={{ padding: '6px 12px', background: '#1a1a1e', border: '1px solid #333', borderRadius: 6, color: '#E8720C', cursor: 'pointer', fontSize: 12 }}>
                  <i className="fas fa-edit" />
                </button>
                <button data-testid={`delete-assistant-${a.id}`} onClick={() => handleDelete(a.id, a.name)}
                  style={{ padding: '6px 12px', background: '#1a1a1e', border: '1px solid #333', borderRadius: 6, color: '#f87171', cursor: 'pointer', fontSize: 12 }}>
                  <i className="fas fa-trash" />
                </button>
              </div>
            </div>
          );
        })}
        {assistants.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: '#555' }}>No hay asistentes registrados</div>
        )}
      </div>
    </div>
  );
};

export default AssistantsPanel;
